import styled from "@emotion/styled";
import type { ReactNode } from "react";
import { Header } from "./Header";
import { HeaderNavigation } from "../molecules/HeaderNavigation";

interface PageLayoutProps {
  /** Título que se muestra en el Header */
  title: string;
  /** Contenido principal de la página */
  children: ReactNode;
  /** Clase CSS opcional para el contenedor */
  className?: string;
  /** Variante del ContentWrapper: 'centered' (max-width 1200px) o 'full' (max-width 100%) */
  variant?: "centered" | "full";
  /** Acción personalizada para el lado derecho del header (por defecto muestra logout) */
  headerRightAction?: ReactNode;
}

const PageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  background: ${(props) => props.theme.colors.tertiary[1000]};
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const ContentWrapper = styled.div<{ variant: "centered" | "full" }>`
  /* Layout centrado con ancho máximo */
  width: 100%;
  max-width: ${(props) => (props.variant === "centered" ? "1200px" : "100%")};
  margin: 0 auto;
  box-sizing: border-box;
  flex: 1;
  display: flex;
  flex-direction: column;

  /* Variante full: gap desde mobile */
  ${(props) =>
    props.variant === "full" &&
    `
    gap: ${props.theme.spacing(2)};

    @media (min-width: 768px) {
      gap: ${props.theme.spacing(3)};
    }

    @media (min-width: 1200px) {
      gap: ${props.theme.spacing(4)};
    }
  `}

  /* 📱 MOBILE: Padding fluido con clamp() */
  padding: ${(props) => props.theme.spacing(2.25)} clamp(1rem, 3vw, ${(props) =>
    props.theme.spacing(3)});
  padding-bottom: ${(props) => props.theme.spacing(16)};

  /* 💻 TABLET+: Padding más amplio */
  ${(props) => props.theme.breakpoints.up("tablet")} {
    padding: ${(props) => props.theme.spacing(2.25)}
      clamp(2rem, 5vw, ${(props) => props.theme.spacing(6)});
    padding-bottom: ${(props) => props.theme.spacing(16)};
  }

  /* 🖥️ DESKTOP+: Padding máximo */
  ${(props) => props.theme.breakpoints.up("desktop")} {
    padding: clamp(2rem, 4vw, ${(props) => props.theme.spacing(4)})
      ${(props) => props.theme.spacing(8)};
    padding-bottom: ${(props) => props.theme.spacing(16)};
  }

  /* Variante full: ajustes específicos de padding */
  ${(props) =>
    props.variant === "full" &&
    `
    @media (min-width: 768px) {
      padding: ${props.theme.spacing(2.25)} ${props.theme.spacing(6)};
      padding-bottom: ${props.theme.spacing(16)};
    }

    @media (min-width: 1200px) {
      max-width: 1200px;
    }
  `}
`;

/**
 * Layout compartido para páginas que incluye:
 * - Header con navegación
 * - Contenedor con padding/margin responsivo
 * - Soporte para scroll vertical
 */
export const PageLayout = ({
  title,
  children,
  className,
  variant = "centered",
  headerRightAction = null,
}: PageLayoutProps) => {
  return (
    <PageContainer className={className}>
      <Header>
        <HeaderNavigation title={title} rightAction={headerRightAction} />
      </Header>

      <ContentWrapper variant={variant}>{children}</ContentWrapper>
    </PageContainer>
  );
};
